import { add, nextSunday, startOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import enUS from 'date-fns/locale/en-US';
import jaJP from 'date-fns/locale/ja';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState, VFC } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { ListingType } from '@/apis/aniftyBackend/mintAsks/interface';
import CustomButton from '@/components/CustomButton';
import FloatingModal from '@/components/FloatingModal';
import useETHPrice from '@/hooks/useETHPrice';
import getThumbnailURL from '@/utils/getThumbnailURL';

import styles from './SellConfirmationModal.module.scss';

type TimeZone = 'Asia/Tokyo' | 'UTC' | 'EST' | 'EDT';

type Props = {
  creation: V2CreationData;
  listingData: {
    supply?: number;
    price: number;
  } | null;
  scheduleSellingEnabled: boolean;
  initialSchedule?: Date;
  error: null | string;
  isOpen: boolean;
  isProcessing: boolean;
  isEditMode?: boolean;
  onRequestClose(): void;
  onConfirm(reservedDate?: { timeZone: TimeZone; localDate: Date }): void;
  listingType: ListingType;
};

const SellConfirmationModal: VFC<Props> = ({
  creation,
  listingData,
  scheduleSellingEnabled,
  initialSchedule,
  error: parentError,
  isOpen,
  isProcessing,
  isEditMode,
  onRequestClose,
  onConfirm,
  listingType,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [timeZone, setTimeZone] = useState<TimeZone>('Asia/Tokyo');

  const { ethToUSD, ethToJPY } = useETHPrice();

  const initialDate = useMemo(() => {
    if (initialSchedule) {
      // Initial schedule is selected
      return initialSchedule;
    }

    // Set initial date to 9 pm of next sunday in JP
    return zonedTimeToUtc(
      add(startOfDay(nextSunday(new Date())), { hours: 21 }),
      'Asia/Tokyo'
    );
  }, []);
  const [reservationDate, setReservationDate] = useState(initialDate);

  // Register datepicker locales
  useEffect(() => {
    registerLocale('en-US', enUS);
    registerLocale('ja-JP', jaJP);
  }, []);

  let error: string | null = null;

  const reservationDateInUTC = zonedTimeToUtc(reservationDate, timeZone);

  const reservedDateTimestamp = reservationDateInUTC.getTime();
  const currentTimestamp = new Date().getTime();

  if (reservedDateTimestamp <= currentTimestamp) {
    error = t('sellConfirmationModal.errors.pastDate');
  }

  const handleRequestClose = () => {
    if (isProcessing) {
      // Cannot close the modal while processing
      return;
    }
    onRequestClose();
  };

  const handleScheduleButtonClick = () => {
    onConfirm({
      timeZone,
      localDate: reservationDate,
    });
  };

  const thumbnailURL = useMemo(
    () =>
      getThumbnailURL(creation.imageURL, {
        cropSize: creation.cropSize,
        cropStartX: creation.cropStartX,
        cropStartY: creation.cropStartY,
        width: creation.width,
        height: creation.height,
      }),
    [creation]
  );

  const price = listingData?.price ?? 0;
  const estimatedPrice = useMemo(() => {
    if (!ethToJPY || !ethToUSD) {
      return `Loading...`;
    }

    if (i18n.language === 'ja') {
      // Display price in JPY
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
      }).format(price * ethToJPY);
    }

    // Display price in USD
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price * ethToUSD);
  }, [price, ethToUSD, ethToJPY]);

  const rarityLabel = useMemo(() => {
    if (!listingData) {
      return null;
    }

    const { supply } = listingData;

    if (supply <= 1) {
      return 'UNIQUE';
    }

    if (supply <= 3) {
      return 'RARE';
    }

    return 'COMMON';
  }, [listingData]);

  const isAuction = listingType === ListingType.Auction;

  return (
    <FloatingModal
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
    >
      <div className={styles.artworkInformationSection}>
        <h3>{t('sellConfirmationModal.artworkInfo.heading')}</h3>
        <div>
          <img
            src={thumbnailURL}
            alt="creation thumbnail"
            className={styles.creationThumbnail}
          />

          <div className={styles.creationProperties}>
            {listingType === ListingType.Sale && (
              <div className={styles.creationProperty}>
                <h4>{t('sellConfirmationModal.artworkInfo.edition')}:</h4>
                <div>{listingData?.supply || 'NOT ENTERED'}</div>
                {rarityLabel && <span>{rarityLabel}</span>}
              </div>
            )}

            <div className={styles.creationProperty}>
              <h4>
                {isAuction
                  ? t('sellConfirmationModal.artworkInfo.reservePrice')
                  : t('sellConfirmationModal.artworkInfo.price')}
                :
              </h4>
              <div>
                {listingData?.price || 'NOT ENTERED'}{' '}
                {isAuction ? 'WETH' : 'ETH'}
              </div>
              <span>{estimatedPrice}</span>
            </div>
          </div>
        </div>
      </div>

      {scheduleSellingEnabled ? (
        <>
          <hr />
          <div className={styles.scheduleSection}>
            <h3>{t('sellConfirmationModal.scheduleSelling.heading')}⏱</h3>
            <p>{t('sellConfirmationModal.scheduleSelling.description')}</p>

            <div className={styles.scheduleForm}>
              <div className={`${styles.inputGroup} ${styles.timeZoneSelect}`}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 9C12.24 9 10 11.24 10 14C10 16.76 12.24 19 15 19C17.76 19 20 16.76 20 14C20 11.24 17.76 9 15 9ZM16.65 16.35L14.5 14.2V11H15.5V13.79L17.35 15.64L16.65 16.35Z"
                    fill="#C4C4C4"
                  />
                  <path
                    d="M10 18V20C4.48 20 0 15.52 0 10C0 4.48 4.48 0 10 0C14.84 0 18.87 3.44 19.8 8H17.73C17.09 5.54 15.33 3.53 13 2.59V3C13 4.1 12.1 5 11 5H9V7C9 7.55 8.55 8 8 8H6V10H8V13H7L2.21 8.21C2.08 8.79 2 9.38 2 10C2 14.41 5.59 18 10 18Z"
                    fill="#C4C4C4"
                  />
                </svg>

                <select
                  onChange={(e) => setTimeZone(e.target.value as TimeZone)}
                  value={timeZone}
                  disabled={isProcessing}
                >
                  <option value="Asia/Tokyo">JST (UTC +9)</option>
                  <option value="UTC">UTC</option>
                  <option value="EST">EST (UTC -5)</option>
                  <option value="EDT">EDT (UTC -4)</option>
                </select>
              </div>

              {/* Date Input */}
              <div className={styles.inputGroup}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_4389_15450)">
                    <path
                      d="M20 3H19V1H17V3H7V1H5V3H4C2.9 3 2 3.9 2 5V21C2 22.1 2.9 23 4 23H20C21.1 23 22 22.1 22 21V5C22 3.9 21.1 3 20 3ZM20 21H4V8H20V21Z"
                      fill="#C4C4C4"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4389_15450">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>

                <DatePicker
                  selected={reservationDate}
                  onChange={(date) => setReservationDate(date as Date)}
                  locale={i18n.language === 'ja' ? 'ja-JP' : 'en-US'}
                  minDate={new Date()}
                  disabled={isProcessing}
                />
              </div>

              {/* Time Input */}
              <div className={styles.inputGroup}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_4389_15457)">
                    <path
                      d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z"
                      fill="#C4C4C4"
                    />
                    <path
                      d="M12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z"
                      fill="#C4C4C4"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4389_15457">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>

                <DatePicker
                  selected={reservationDate}
                  onChange={(date) => setReservationDate(date as Date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  dateFormat="h:mm aa"
                  showPopperArrow={false}
                  disabled={isProcessing}
                />
              </div>

              {error && <div className={styles.error}>{error}</div>}

              {parentError && <div className={styles.error}>{parentError}</div>}

              {isEditMode ? (
                <CustomButton
                  variant="secondary"
                  onClick={handleScheduleButtonClick}
                  loading={isProcessing}
                  className={styles.confirmButton}
                  disabled={!!error}
                >
                  {t(
                    'sellConfirmationModal.scheduleSelling.editConfirmButtonText'
                  )}
                </CustomButton>
              ) : (
                <CustomButton
                  variant="primary"
                  onClick={handleScheduleButtonClick}
                  loading={isProcessing}
                  className={styles.confirmButton}
                  disabled={!!error}
                >
                  {isAuction
                    ? t(
                        'sellConfirmationModal.scheduleSelling.confirmButtonTextAuction'
                      )
                    : t(
                        'sellConfirmationModal.scheduleSelling.confirmButtonText'
                      )}
                </CustomButton>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className={styles.postSellingConfirmation}>
          {parentError && <div className={styles.error}>{parentError}</div>}
          <CustomButton
            variant="primary"
            loading={isProcessing}
            onClick={() => onConfirm()}
            className={styles.confirmButton}
          >
            {isAuction
              ? t('sellConfirmationModal.postAuctionButtonText')
              : t('sellConfirmationModal.postSellingButtonText')}
          </CustomButton>
        </div>
      )}

      <div className={styles.gasFeeNotice}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 0H2C0.9 0 0.00999999 0.9 0.00999999 2L0 20L4 16H18C19.1 16 20 15.1 20 14V2C20 0.9 19.1 0 18 0ZM11 9H9V3H11V9ZM11 13H9V11H11V13Z"
            fill="#F3592F"
          />
        </svg>

        <p>
          <span>{t('sellConfirmationModal.note.note')}:</span>{' '}
          <Trans
            i18nKey="sellConfirmationModal.note.content"
            components={{ strong: <strong /> }}
          />
        </p>
      </div>
    </FloatingModal>
  );
};

export default SellConfirmationModal;
